import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { usePrevious } from 'react-use'
import clsx from 'clsx'
import { useAtom } from 'jotai'

import { AppBreakpoint } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { randomAppIcon } from '@makyoapp/makyo-common'
import { ArrowLeft16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { AppIcon, Button, ButtonVariant, Tip, useTip } from '@makyoapp/makyo-ui'

import { toolbarAtom } from '@/lib/jotai'
import { lazyload } from '@/lib/lazy-load'
import { APP_VERSION } from '@/configs/version'
import { AppProjectState, useAppContext } from '@/context/AppContext'
import { useCacheContext } from '@/context/CacheContext'
import { useOrgsContext } from '@/context/OrgsContext'
import { useActiveDevice } from '@/hooks/useActiveDevice'
import useAuth from '@/hooks/useAuth'
import { BUILDER_DEVICES } from '@/shared/builder-constants'
import { ORG_APP_ROUTE, ORG_ROUTE, PROJECT_ROUTE } from '@/shared/constants'
import { EVENT_KEYS } from '@/shared/custom-event'

import { ReactComponent as LogoFull } from '@/assets/makyo-logo-full.svg?react'

import { NotificationMenu } from './NotificationMenu'
import { OrgMenu } from './OrgMenu'

const VersionHistoryToolbar = lazyload(() => import('./VersionHistoryToolbar'))

function getImageUrl(iconUrl: string | undefined, hasChanged: boolean, withCache: boolean): string | undefined {
  const version = hasChanged ? new Date().getTime() : null
  return iconUrl && iconUrl.startsWith('http') && withCache && version ? `${iconUrl}?v=${version}` : iconUrl
}

function hasProjectChanged(currentProject: AppProjectState | undefined, previousProject: AppProjectState): boolean {
  return JSON.stringify(currentProject) !== JSON.stringify(previousProject)
}

const Logo = () => {
  // get current org id from the url (o/:orgId/*)
  const orgId = window.location.pathname.startsWith(ORG_ROUTE) ? window.location.pathname.split('/')[2] : undefined

  const { orgs } = useOrgsContext()
  const { userData } = useAuth()
  const myOrg = orgs.find((o) => o.ownerId === userData?.id && o.isDefault)

  let link = '/'

  if (myOrg) {
    link = `${ORG_ROUTE}/${orgId ? orgId : myOrg.id}/${ORG_APP_ROUTE}`
  }

  return (
    <Link
      to={link}
      className={clsx(
        'flex justify-center items-center text-primary-600',
        'transition-colors border-r-trans hover:bg-zinc-900 dark:hover:bg-slate-700',
        'relative'
      )}
    >
      <LogoFull height={32} width={169} className='px-4 fill-primary-500' />
      <span className='absolute top-0.5 right-1.5 text-white text-m-ss rounded-sm'>v{APP_VERSION}</span>
    </Link>
  )
}

interface DeviceButtonProps {
  id: AppBreakpoint
  Icon: (props: React.ComponentProps<'svg'>) => JSX.Element
  label: string
  isActive: boolean
  setActiveDevice: (id: AppBreakpoint) => void
}

const DeviceButton = ({ id, Icon, label, isActive, setActiveDevice }: DeviceButtonProps) => {
  const { x, y, strategy, refs, context, placement, isOpen, arrowRef, getReferenceProps, getFloatingProps } = useTip()

  const handleClick = useCallback(() => {
    setActiveDevice(id)
  }, [id, setActiveDevice])

  return (
    <>
      <div
        role='button'
        ref={refs.setReference}
        onClick={handleClick}
        className={clsx('h-full aspect-square flex items-center justify-center', isActive && 'bg-slate-700')}
        {...getReferenceProps()}
      >
        <Icon className='w-4 h-4' />
      </div>
      {isOpen && (
        <Tip
          x={x}
          y={y}
          placement={placement}
          strategy={strategy}
          ref={refs.setFloating}
          {...getFloatingProps()}
          context={context}
          arrowRef={arrowRef}
        >
          {label}
        </Tip>
      )}
    </>
  )
}

const BuilderDevices = () => {
  const [device, setActiveDevice] = useActiveDevice()

  const devices = Object.values(BUILDER_DEVICES)

  return (
    <div className='grow flex justify-center w-full px-4 animate-show'>
      {devices.map(({ id, Icon, label }) => {
        return (
          <DeviceButton
            key={id}
            id={id}
            Icon={Icon}
            label={label}
            isActive={device === id}
            setActiveDevice={setActiveDevice}
          />
        )
      })}
    </div>
  )
}

const ProjectToolbar = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { imageCache } = useCacheContext()
  const { project, projectAction } = useAppContext()

  const [toolbar] = useAtom(toolbarAtom)
  const location = useLocation()
  const [isAuthorized, setIsAuthorized] = useState(false)

  useEffect(() => {
    // listen to the org authorization change event
    const handleOrgAuthorizationChange = (event: Event) => {
      setIsAuthorized((event as CustomEvent).detail.isAuthorized)
    }

    window.addEventListener(EVENT_KEYS.ORG_AUTHORIZATION_CHANGE, handleOrgAuthorizationChange)
    return () => window.removeEventListener(EVENT_KEYS.ORG_AUTHORIZATION_CHANGE, handleOrgAuthorizationChange)
  }, [])

  const previousProject = usePrevious(project)
  const hasChanged = previousProject ? hasProjectChanged(project, previousProject) : true
  const withCache = !!project && !!imageCache.values[project.id]
  const projectIcon = getImageUrl(project?.icon, hasChanged, withCache)
  const isUrl = project?.icon.startsWith('http') || project?.icon.startsWith('blob')
  const title = project?.title || ''

  const isLoading = project?.loading

  const handleOpenVersionHistory = () => {
    window.dispatchEvent(new CustomEvent(EVENT_KEYS.OPEN_VERSION_HISTORY))
  }

  if (!project || !isAuthorized) return <Logo />

  return (
    <>
      <button
        className='h-full w-12 shrink-0 flex-cc hover:bg-slate-600 transition-colors'
        onClick={() => navigate(`${ORG_ROUTE}/${project?.orgId}/${ORG_APP_ROUTE}`)}
      >
        <ArrowLeft16 />
      </button>

      <div className={clsx('px-4 flex-c grow-0 shrink-0 min-w-0 text-white justify-between')}>
        <div className='flex-c overflow-hidden basis-[180px] shrink-0'>
          {isLoading ? (
            <div className='flex items-center gap-2'>
              <div className='animate-pulse bg-zinc-300 dark:bg-slate-700 size-9 rounded-lg' />
              <div className='space-y-2'>
                <div className='animate-pulse bg-zinc-300 dark:bg-slate-700 w-18 h-2.5 rounded-sm' />
                <div className='animate-pulse bg-zinc-300 dark:bg-slate-700 w-18 h-2 rounded-sm' />
              </div>
            </div>
          ) : (
            <AppIcon
              className={clsx('w-9 h-9 flex-cc overflow-hidden')}
              noPad={isUrl}
              style={{ background: project.color }}
              title={title}
              defaultSize={false}
              icon={projectIcon || randomAppIcon(title)}
              withCache={withCache}
              imgProps={{
                className: 'w-full h-full object-cover',
              }}
            />
          )}

          {isLoading ? null : (
            <div className='flex min-w-0 flex-col overflow-hidden justify-center'>
              <div className='txt font-bold'>{title}</div>
              <VersionHistoryToolbar />
            </div>
          )}
        </div>

        {isLoading ? (
          <div className='shrink-0 animate-pulse bg-zinc-300 dark:bg-slate-700 size-8 rounded-sm' />
        ) : (
          <div className='shrink-0'>
            <Button
              title={t('Open Version History')}
              noPadding
              squared
              variant={ButtonVariant.Outlined}
              icon={<MaterialIcon icon='deployed_code_history' size={16} />}
              onClick={handleOpenVersionHistory}
            />
          </div>
        )}

        {isLoading ? (
          <div className='flex gap-2'>
            <div className='animate-pulse bg-zinc-300 dark:bg-slate-700 w-28 h-8 rounded-sm' />
            <div className='flex gap-1.5'>
              <div className='animate-pulse bg-zinc-300 dark:bg-slate-700 w-21 h-8 rounded-sm' />
              <div className='animate-pulse bg-zinc-300 dark:bg-slate-700 w-20 h-8 rounded-sm' />
            </div>
          </div>
        ) : (
          projectAction
        )}
      </div>

      {toolbar.showDevices && <BuilderDevices />}

      {toolbar.components.length > 0 && (
        <div className='dark gap-4 mk-toolbar-navs flex items-center justify-end w-full'>
          {toolbar.components.map(({ path, component }, i) => {
            const shouldShow = location.pathname.includes(path)
            return <React.Fragment key={i}>{shouldShow ? component : null}</React.Fragment>
          })}
        </div>
      )}
    </>
  )
}

/**
 * main toolbar
 */
export const ToolbarV2 = () => {
  const location = useLocation()
  const isProject = location.pathname.startsWith(PROJECT_ROUTE)

  const [showToolbar, setShowToolbar] = useState(true)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl (Windows) or Cmd (macOS)
      const isCtrlOrCmd = event.ctrlKey || event.metaKey

      // Arrow Up to toggle both
      if (isProject && event.code === 'ArrowUp' && isCtrlOrCmd && !event.altKey && !event.shiftKey) {
        event.preventDefault()
        setShowToolbar((prev) => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isProject])

  return (
    <div className={clsx('h-12 text-white bg-slate-900 sticky z-10 flex flex-row', !showToolbar && 'hidden')}>
      {isProject ? <ProjectToolbar /> : <Logo />}

      {/* This grow div will push the notification and org menu to the right */}
      <div className='grow' />

      {!isProject && <div className='basis-2/4 grow'></div>}
      <div className='flex-c justify-end'>
        <NotificationMenu />
        <OrgMenu />
      </div>
    </div>
  )
}
